import React from "react";

const {
  useEffect: useEffect_H8WF,
  useRef: useRef_H8WF,
  useState: useState_H8WF,
} = React;

const S06_TALENT_TEAM_SERVICES = [
  ["01", "EOR", "Employment support for eligible roles, locations, and agreed responsibilities."],
  ["02", "Managed Workforce", "A coordinated workforce, shaped around the work and an agreed service model."],
  ["03", "Dedicated Team", "A dedicated team with agreed roles, communication, quality control, and review."],
  ["04", "Project Delivery Support", "Extra coordination for progress, quality, risks, and resources in a defined delivery."],
];

function useH8WFLifecycle(sectionRef) {
  const [state, setState] = useState_H8WF({ intersecting: false, visible: true, reduced: false });
  useEffect_H8WF(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let intersecting = false;
    let initialFrame = null;

    const sync = () => setState({
      intersecting,
      visible: document.visibilityState === "visible",
      reduced: motionQuery.matches,
    });
    const measure = () => {
      const rect = section.getBoundingClientRect();
      const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      intersecting = rect.height > 0 && visibleHeight / rect.height >= 0.25;
      sync();
    };
    const observer = new IntersectionObserver(([entry]) => {
      intersecting = entry.isIntersecting && entry.intersectionRatio >= 0.25;
      sync();
    }, { threshold: [0, 0.25, 1] });

    observer.observe(section);
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("resize", measure);
    motionQuery.addEventListener("change", sync);
    initialFrame = window.requestAnimationFrame(measure);

    return () => {
      if (initialFrame !== null) window.cancelAnimationFrame(initialFrame);
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("resize", measure);
      motionQuery.removeEventListener("change", sync);
    };
  }, [sectionRef]);

  return { ...state, canAnimate: state.intersecting && state.visible && !state.reduced };
}

function TalentTeamSolutionsVariant() {
  const sectionRef = useRef_H8WF(null);
  const lifecycle = useH8WFLifecycle(sectionRef);

  return (
    <section
      className="section-pad s06-talent-team-section"
      id="workforce"
      ref={sectionRef}
      aria-labelledby="s06-talent-team-title"
      data-s06-motion-active={lifecycle.canAnimate ? "true" : "false"}
      data-s06-reduced-motion={lifecycle.reduced ? "true" : "false"}
    >
      <style>{`
        .s06-talent-team-section { position:relative; overflow:clip; background:var(--paper-inset); }
        .s06-talent-team-intro { display:grid; grid-template-columns:minmax(0,1.22fr) minmax(280px,.58fr); gap:74px; align-items:end; margin:22px 0 48px; }
        .s06-talent-team-intro h2 { max-width:770px; margin:0; font-family:var(--font-serif); font-size:clamp(48px,5.4vw,80px); font-weight:500; line-height:.94; letter-spacing:-.065em; }
        .s06-talent-team-intro .lead { max-width:420px; margin:0 0 4px; color:var(--fg-muted); font-size:16px; line-height:1.56; }
        .s06-talent-team-stage { display:grid; grid-template-columns:1.23fr .77fr; min-height:576px; overflow:hidden; border:1px solid var(--line); border-radius:27px; background:var(--paper); }
        .s06-talent-team-visual { position:relative; min-height:100%; overflow:hidden; background:var(--paper-2); }
        .s06-talent-team-visual img { display:block; width:100%; height:100%; object-fit:cover; object-position:50% 50%; transform:scale(1.015); }
        .s06-talent-team-visual::after { position:absolute; inset:0; background:linear-gradient(180deg,rgba(15,25,43,.025) 18%,rgba(15,25,43,.48) 100%); content:""; pointer-events:none; }
        .s06-talent-team-flow { position:absolute; inset:0; z-index:1; width:100%; height:100%; pointer-events:none; }
        .s06-talent-team-flow path { fill:none; stroke:var(--indigo); stroke-width:2.2; stroke-linecap:round; stroke-dasharray:4 12; filter:drop-shadow(0 1px 1px rgba(255,255,255,.8)); animation:s06-talent-team-travel 12s linear infinite; }
        .s06-talent-team-flow circle { fill:var(--indigo); filter:drop-shadow(0 0 8px color-mix(in oklch,var(--indigo) 55%,transparent)); animation:s06-talent-team-pulse 3s ease-in-out infinite; transform-origin:50% 50%; }
        .s06-talent-team-label { position:absolute; z-index:2; left:32px; bottom:28px; color:#fff; font:650 10px/1.25 var(--font-mono); letter-spacing:.14em; }
        .s06-talent-team-label strong { display:block; margin-top:9px; font-family:var(--font-serif); font-size:28px; font-weight:500; line-height:1; letter-spacing:-.045em; }
        .s06-talent-team-services { display:flex; flex-direction:column; padding:37px 40px 27px; }
        .s06-talent-team-services > .mono { margin-bottom:23px; color:var(--fg-subtle); font-size:9px; font-weight:650; letter-spacing:.15em; }
        .s06-talent-team-service { display:grid; grid-template-columns:31px minmax(0,1fr); column-gap:14px; padding:21px 0 20px; border-top:1px solid color-mix(in oklch,var(--fg) 10%,transparent); }
        .s06-talent-team-service:last-of-type { border-bottom:1px solid var(--line); }
        .s06-talent-team-service b { padding-top:4px; color:var(--indigo); font:650 10px/1 var(--font-mono); opacity:.86; }
        .s06-talent-team-service h3 { margin:0; color:var(--fg); font-size:16px; font-weight:520; line-height:1.15; letter-spacing:-.038em; }
        .s06-talent-team-service p { grid-column:2; max-width:270px; margin:7px 0 0; color:var(--fg-subtle); font-size:11.5px; line-height:1.48; }
        .s06-talent-team-scope { margin:auto 0 0; padding-top:20px; color:var(--fg-subtle); font-size:10px; line-height:1.48; }
        .s06-talent-team-continuation { display:flex; align-items:center; justify-content:space-between; gap:20px; padding:21px 2px 0; color:var(--fg-muted); font-size:13px; }
        .s06-talent-team-continuation a { display:inline-flex; gap:10px; color:var(--fg); font-weight:650; text-decoration:none; }
        .s06-talent-team-continuation a:hover { color:var(--indigo); }
        .s06-talent-team-continuation a:focus-visible { outline:2px solid var(--indigo); outline-offset:4px; }
        .s06-talent-team-section[data-s06-motion-active="false"] .s06-talent-team-flow * { animation-play-state:paused !important; }
        .s06-talent-team-section[data-s06-reduced-motion="true"] .s06-talent-team-flow * { animation:none !important; }
        @keyframes s06-talent-team-travel { to { stroke-dashoffset:-160; } }
        @keyframes s06-talent-team-pulse { 50% { transform:scale(1.36); opacity:.72; } }
        @media (prefers-reduced-motion:reduce) { .s06-talent-team-flow * { animation:none !important; } }
        @media (max-width:720px) {
          .s06-talent-team-section { overflow:hidden; }
          .s06-talent-team-section .container { padding-right:18px; padding-left:18px; }
          .s06-talent-team-intro { grid-template-columns:1fr; gap:22px; margin:22px 0 30px; }
          .s06-talent-team-intro h2 { font-size:52px; }
          .s06-talent-team-intro .lead { max-width:unset; }
          .s06-talent-team-stage { grid-template-columns:1fr; min-height:0; border-radius:22px; }
          .s06-talent-team-visual { height:320px; min-height:0; }
          .s06-talent-team-label { left:22px; bottom:22px; }
          .s06-talent-team-label strong { font-size:24px; }
          .s06-talent-team-services { padding:28px 22px 24px; }
          .s06-talent-team-service { grid-template-columns:28px minmax(0,1fr); column-gap:10px; padding:18px 0; }
          .s06-talent-team-service h3 { font-size:16px; }
          .s06-talent-team-continuation { align-items:flex-start; flex-direction:column; }
        }
      `}</style>

      <div className="container">
        <div className="eyebrow">WORKFORCE &amp; TEAM SOLUTIONS</div>
        <header className="s06-talent-team-intro">
          <h2 id="s06-talent-team-title">Build the talent, team, and operating model behind the work.</h2>
          <p className="lead">The right technology needs the right people, team structure, and day-to-day operating model. We clarify the combination that fits the work before scope and responsibility are agreed.</p>
        </header>

        <div className="s06-talent-team-stage">
          <div className="s06-talent-team-visual">
            <img src="/assets/section-06-talent-team-solutions/talent-team-operating-model-v2.png" alt="Illustrative team operating model" />
            <svg className="s06-talent-team-flow" viewBox="0 0 1000 750" preserveAspectRatio="none" aria-hidden="true">
              <path d="M500 374 C500 334 496 307 498 274" />
              <path d="M498 374 C454 380 419 370 370 372" />
              <path d="M502 374 C551 384 594 377 648 386" />
              <circle cx="500" cy="374" r="5" />
            </svg>
            <div className="s06-talent-team-label">ILLUSTRATIVE OPERATING MODEL<strong>One team.<br />Clear operating rhythm.</strong></div>
          </div>
          <div className="s06-talent-team-services">
            <span className="mono">TALENT &amp; TEAM SOLUTIONS</span>
            {S06_TALENT_TEAM_SERVICES.map(([number, title, body]) => (
              <article className="s06-talent-team-service" key={title}>
                <b>{number}</b><h3>{title}</h3><p>{body}</p>
              </article>
            ))}
            <p className="s06-talent-team-scope">Availability, geography, eligibility, pricing, and responsibilities are confirmed for each engagement.</p>
          </div>
        </div>

        <footer className="s06-talent-team-continuation">
          <span>Digital layers for talent and workforce operations.</span>
          <a href="#our-products">Explore Product Ecosystem <span aria-hidden="true">→</span></a>
        </footer>
      </div>
    </section>
  );
}

function WorkforceTeamSolutions() {
  return <TalentTeamSolutionsVariant />;
}

export { WorkforceTeamSolutions };
